import { useMemo, useState, useEffect, type FC, useRef } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  COLOR_YELLOW,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor, useSublineColor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { FlexBox, Image as ImageComp } from 'shared/components/Common';
import { HoverableDropzone } from '../HoverableDropzone';

import SvgIcons from 'shared/components/Common/IconsHeroDetailed';
import useGeneralActions from '../hooks/useGeneralActions';
import ButtonActionModal from 'shared/components/Common/ButtonActionModal';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  flex-direction: column;

  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 128px 0 0px 0px;
  max-width: 100%;
  gap: 65px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
   padding: 78px 0 0px 0px;
   gap: 48px;
    `}
`;

const TitleWrapper = styled.div`
  position: relative;
  z-index: 7;
  display: flex;
  justify-content: space-between;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  width: 49%;
  margin-top: 0px;
  margin-bottom: 0px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  line-height: 1.35em;
  color: #fff;
  width: 46%;
  padding-top: 8px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 59px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
      text-align: center;
      margin-top: 30px;
      margin-bottom: 0px;
      `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;
  z-index: 12;
  margin: 0 auto;

  @media screen and (max-width: 575px) {
    width: 100%;
    z-index: 12;
    margin: 0 auto;
    display: flex;
    justify-content: center;
  }
`;

const StyledButton = styled(Button)`
  max-width: 425px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    max-width: 360px;
    width: 100%;
    padding-top: 24px;
    padding-bottom: 26px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    max-width: 300px;

  `}
`;

const SvgArrow = styled.svg`
  position: absolute;
  right: -116px;
  width: 90px;
  top: -40px;
  opacity: 0.42;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const BenefitWrapper = styled.div`
  position: relative;
  margin-top: 28px;
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 24;
  padding: 50px 0 90px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    bottom: auto;
    padding-bottom: 80px;
    padding-top: 50px;
    `}
`;

const BenefitItem = styled.div`
  width: 31.5%;
  padding: 24px;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  border: 1px solid rgb(0 0 0 / 16%);

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 32.5%;
    padding: 18px 18px 20px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    padding: 18px 15px;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const StyledSvgIcons = styled(SvgIcons)`
  width: 62px;
  height: 62px;
  margin-right: 18px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 52px;
      height: 52px;
      margin-bottom: 8px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 52px;
    height: 52px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 78%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 6px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(88px);
    background: #fff;
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.13;
    bottom: -20%;
    right: -44%;
  }

  .hero-white-v5_two {
    width: 522px;
    height: 522px;
    opacity: 0.2;
    background: #fff;
    right: -38%;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 409px;
    height: 909px;
    opacity: 0.1;
    background: #fff;
    right: -28%;
    bottom: 30%;
    rotate: -45deg;
  }

  .hero-white-v5_four {
    width: 1307px;
    height: 1307px;
    opacity: 0.2;
    background: #fff;
    left: -52%;
    bottom: -74%;
  }

  .hero-white-v5_five {
    width: 322px;
    height: 722px;
    opacity: 0.08;
    background: #fff;
    left: -68%;
    top: 1%;
    rotate: -45deg;
  }

  .hero-white-v5_six {
    width: 322px;
    height: 722px;
    opacity: 0.05;
    background: #000;
    right: -78%;
    top: 1%;
    rotate: -45deg;
  }
`;

const ColoredBg = styled.div<{ isSingle?: boolean }>`
  position: absolute;
  width: 100%;
  height: 60%;
  top: 0;
  border-top: 1px solid rgb(255 255 255 / 16%);
`;

const DottedBg = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  bottom: 0;
`;

const DotsBg = styled.div<{ isWhiteBg?: boolean }>`
  background: url('https://pagewheel-public.s3.us-east-1.amazonaws.com/imgs/whiteDot_1.svg') repeat
    center center;
  width: 100%;
  height: 560px;
  position: absolute;
  bottom: 0;
  opacity: 0.05;
  ${(props) => props.isWhiteBg && `filter: brightness(0) invert(0); opacity: 0.035;`}
`;

const ImageGallery = styled.div<{ count: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1540px;
  transform-style: preserve-3d;
  padding-top: ${({ theme: { $width } }) => ($width <= 991 ? '90px' : '120px')};
  z-index: 22;
  margin: 0 auto;
  left: 0.1%;
  gap: 10px;
  max-width: 2130px;
  width: 81%;
  position: relative;
  justify-content: space-between;

  .det-hero-v6 {
    width: 21.5vw;
    aspect-ratio: 1 / 1.4142;
    margin: 0 10px;
    position: relative;
    filter: drop-shadow(10px 10px 20px rgba(0, 0, 0, 0.22));
    display: flex;
    justify-content: center;
    align-items: center;
    transform-style: preserve-3d;
    will-change: transform;

    @media screen and (max-width: 575px) {
      filter: drop-shadow(2px 10px 10px rgba(0, 0, 0, 0.22));
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  ${({ count }) =>
    count < 2
      ? `
        display: flex;
        justify-content: center;

        .det-hero-v6 {
            width: fit-content;
            aspect-ratio: auto;
            margin: 0;
         }
      `
      : count === 2
        ? `
      max-width: 1360px;
      width: 100%;
      justify-content: center;
      margin-bottom: 70px;

  .det-hero-v6 {
    width: 21.5vw;
    aspect-ratio: 1 / 1.4142;
    position: relative;
    filter: drop-shadow(10px 10px 20px rgba(0, 0, 0, 0.22));
    display: flex;
    justify-content: center;
  }

  .det-hero-v6:nth-child(1) {
    transform: rotateY(30deg) scale(1.04) translateX(-20px) translate3d(-50px, 7px, 20px);
    z-index: 2;
  }

  .det-hero-v6:nth-child(2) {
    transform: rotateY(0deg) scale(1.1);
    z-index: 3;
  }

  .det-hero-v6:nth-child(3) {
    transform: rotateY(-30deg) scale(1.04) translateX(20px) translate3d(50px, 7px, 20px);
    z-index: 2;
  }

  @media screen and (max-width: 1560px) {
    perspective: 1340px;
    width: 86%;
    gap: 15px;
    .det-hero-v6 {
      width: 23vw;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1.15) translateX(-20px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1.15) translateX(20px) translate3d(50px, 7px, 20px);
    }
  }

  @media screen and (max-width: 1380px) and (min-width: 1000px) {
    gap: 10px;
    .det-hero-v6 {
      width: 29vw;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1.08) translateX(-40px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1.08) translateX(40px) translate3d(50px, 7px, 20px);
    }
  }

  @media screen and (max-width: 991px) {
    width: 100%;
    gap: 0;
    perspective: 1000px;

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1.5) translateX(-70px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(2) {
      transform: scale(1.0);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1.5) translateX(70px) translate3d(50px, 7px, 20px);
    }

  }

  @media screen and (max-width: 465px) {
    margin-bottom: 40px;

    .det-hero-v6 {
      width: 220px;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(3.5) translateX(33px) translate3d(-60px, 0px, 20px);
    }

    .det-hero-v6:nth-child(2) {
      transform: rotateY(0deg) scale(1.0);
      width: 90vw;
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(3.5) translateX(-33px) translate3d(60px, 0px, 20px);
    }
  }
    `
        : `
      .det-hero-v6:nth-child(1) {
        @media screen and (min-width: 1921px) {
          transform: rotateY(30deg) scale(1.12) translateX(9px) translate3d(-67px, 7px, 20px);
          overflow: hidden;
          z-index: 3;
        }

        transform: rotateY(30deg) scale(1.16) translateX(-4px) translate3d(-67px, 7px, 20px);
        overflow: hidden;
        z-index: 3;


      }

      .det-hero-v6:nth-child(2) {
       @media screen and (min-width: 1921px) {
        transform: rotateY(20deg) translateX(0px) scale(1.03);
        overflow: hidden;
        z-index: 2;
       }

        transform: rotateY(20deg) translateX(-14px) scale(1.05);
        overflow: hidden;
        z-index: 2;
       
      }
      .det-hero-v6:nth-child(3) {
        transform: rotateY(0deg) scale(1);
      }
      .det-hero-v6:nth-child(4) {
        @media screen and (min-width: 1921px) {
          transform: rotateY(-20deg) translateX(0px) scale(1.03);
          overflow: hidden;
          z-index: 2;
       }

        transform: rotateY(-20deg) translateX(14px) scale(1.05);
        overflow: hidden;
        z-index: 2;
      }
      .det-hero-v6:nth-child(5) {
        @media screen and (min-width: 1921px) {
          transform: rotateY(-30deg) scale(1.12) translateX(-9px) translate3d(67px, 7px, 20px);
          overflow: hidden;
          z-index: 3;
        }

        transform: rotateY(-30deg) scale(1.16) translateX(4px) translate3d(67px, 7px, 20px);
        overflow: hidden;
        z-index: 3;
      }

      @media screen and (min-width: 1921px) {
        padding-top: 90px;
      }

      @media screen and (max-width: 1870px) {
        width: 80%;
        gap: 10px;
        perspective: 1240px;
        left: 0.1%;

        .det-hero-v6 {
          width: 24vw;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-4px) scale(1.05);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(4px) scale(1.05);
        }
      }

      @media screen and (max-width: 1720px) {
        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-12px) scale(1.05);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(12px) scale(1.05);
        }
      }

      @media screen and (max-width: 1560px) {
        width: 86%;
        gap: 20px;
        perspective: 1440px;
        left: 0.17%;
        justify-content: center;

        .det-hero-v6 {
          width: 21.5vw;
          z-index: 2;
        }

        .det-hero-v6:nth-child(1) {
          transform: rotateY(30deg) translateX(20px) scale(1.16) translate3d(-67px, 7px, 20px);
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-22px) scale(1.05);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(22px) scale(1.05);
        }

        .det-hero-v6:nth-child(5) {
          transform: rotateY(-30deg) translateX(13px) scale(1.16) translate3d(36px, 7px, 20px);
        }
      
        // safari styles
        .det-hero-v6_safari:nth-child(2) {
          transform: rotateY(20deg) translateX(-7px) scale(1.05);
        }

        .det-hero-v6_safari:nth-child(4) {
          transform: rotateY(-20deg) translateX(7px) scale(1.05);
        }
      }

      @media screen and (max-width: 1380px) {
        width: 100%;
        gap: 0px;
        padding-top: 110px;

        .det-hero-v6 {
          width: 31.5vw;
        }

        .det-hero-v6:nth-child(1) {
          transform: rotateY(30deg) translateX(1.3vw) scale(1.2) translate3d(36px, 9px, -20px);
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(4vw) scale(1.05);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(-4vw) scale(1.05);
        }

        .det-hero-v6:nth-child(5) {
          transform: rotateY(-30deg) translateX(-8.5vw) scale(1.2) translate3d(36px, 9px, -20px);
        }
      }

      @media screen and (max-width: 991px) {
        width: 100%;
        gap: 0;
        perspective: 1000px;

        .det-hero-v6:nth-child(1),
        .det-hero-v6:nth-child(5) {
          display: none;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-8vw) scale(1.05);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(8vw) scale(1.05);
        }
      }

      @media screen and (max-width: 600px) {
        .det-hero-v6 {
          width: 220px;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-8vw) scale(1.65);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(8vw) scale(1.65);
        }
      }
    `}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  width: 450px;
  height: 590px;

  @media screen and (max-width: 1560px) {
    width: 400px;
    height: 520px;
    z-index: 4;
  }

  @media screen and (max-width: 991px) {
    width: 400px !important;
    height: 520px !important;
    z-index: 4;
  }

  @media screen and (max-width: 600px) {
    width: 400px !important;
    height: 540px !important;
    z-index: 4;
  }
`;

const imageStylesOnlyMockup = css`
  position: relative;
  height: 685px;
  width: 513px;

  @media screen and (max-width: 1560px) {
    height: 655px;
    width: 484px;
    z-index: 4;
  }

  @media screen and (max-width: 1480px) {
    width: 400px;
    height: 540px;
    z-index: 4;
  }

  @media screen and (max-width: 991px) {
    width: 80vw !important;
    height: 540px !important;
    z-index: 4;
  }

  @media screen and (max-width: 600px) {
    width: 130vw !important;
    height: 640px !important;
    z-index: 4;
  }
`;

const BookMediaImg = styled(ImageComp)<{ isImageUploaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  border-radius: ${(props) => (props.isImageUploaded ? '5px' : '0px')};
`;

export const V6: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    secondaryColor = COLOR_YELLOW,
    customBackgroundColor,
    oppositeBackgroundColor,
    textColor,
    benefits = [],
    meta,
    hidden,
    pageImages
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, updateImageMeta, purchaseElementId, type, renderSales } = useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });
  const [isButtonActionModalOpen, setIsButtonActionmodalOpen] = useState(false);
  const [sublineColor, setSublineColor] = useState<string>('#fff');
  const topRef = useRef<HTMLDivElement>(null);

  useSublineColor(topRef, setSublineColor, renderSales);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  //check if browser is safari
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isSafariBrowser =
      ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
    setIsSafari(isSafariBrowser);
  }, []);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      setIsImageUploaded(true);
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const handleUpload = async (file: File) => {
    setIsLoading(true);
    await handleUploadAction(file, setResize, 'book_mockup');
    setIsLoading(false);
  };

  const galleryimages = useMemo(() => {
    if ((pageImages ?? []).length === 0 || (pageImages ?? []).length === 1 || !pageImages) {
      return [];
    }

    if (pageImages.length <= 3 || pageImages.length === 2)
      return pageImages.slice(0, 2).map((page) => page);

    if (pageImages.length > 3) return pageImages.slice(0, 4).map((page) => page);

    return [];
  }, [pageImages]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor ? customBackgroundColor : secondaryColor;
  }, [secondaryColor, customBackgroundColor]);

  const iconColor = useMemo(() => {
    if (
      meta?.['background_color']?.includes('background_color4') ||
      !meta?.['background_color']?.startsWith('color')
    ) {
      return oppositeBackgroundColor || secondaryColor;
    }

    return resolvedBackgroundColor || secondaryColor;
  }, [meta, oppositeBackgroundColor, secondaryColor, resolvedBackgroundColor]);

  return (
    <HeroDetailed
      ref={topRef}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: sublineColor || '#fff'
      }}>
      <StyledWrapper>
        <TitleWrapper>
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
            }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="title"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isTitleEmpty}
          />
          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="content"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isContentEmpty}
          />
        </TitleWrapper>
        <ButtonInner>
          <FlexBox position="relative">
            {isButtonActionModalOpen && editable ? (
              <ButtonActionModal
                onClose={() => setIsButtonActionmodalOpen(false)}
                meta={meta}
                cssContainer={css`
                  position: absolute;
                  top: -450px;
                  left: -40px !important;
                  transform: translateY(100%);
                `}
              />
            ) : null}
            {meta['url'] || type === 'delivery' ? (
              <Download
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => (editable ? setIsButtonActionmodalOpen(true) : null)}
              />
            ) : (
              <>
                <StyledButton
                  to={purchaseElementId}
                  button={button}
                  meta={meta}
                  outerOnClick={() => (editable ? setIsButtonActionmodalOpen(true) : null)}
                />
                <SvgArrow xmlns="http://www.w3.org/2000/svg" viewBox="0 0 67 79">
                  <path
                    d="M60.8543 1.92501C60.735 1.38576 61.0755 0.85191 61.6147 0.732616C62.154 0.613323 62.6878 0.953762 62.8071 1.49301L60.8543 1.92501ZM0.00200166 71.4147L10.4952 66.5953L9.42232 78.0923L0.00200166 71.4147ZM2.1027 70.6064C2.65292 70.6541 3.06029 71.1388 3.01258 71.689C2.96488 72.2393 2.48016 72.6466 1.92994 72.5989L2.1027 70.6064ZM5.86516 72.8873C5.31385 72.8545 4.89348 72.381 4.92624 71.8297C4.959 71.2784 5.43249 70.858 5.9838 70.8908L5.86516 72.8873ZM9.87321 71.0644C10.4253 71.0806 10.8596 71.5413 10.8434 72.0933C10.8272 72.6454 10.3666 73.0797 9.81453 73.0635L9.87321 71.0644ZM13.7475 73.115C13.1952 73.117 12.7458 72.671 12.7437 72.1187C12.7416 71.5665 13.1877 71.1171 13.74 71.115L13.7475 73.115ZM17.6492 71.0285C18.201 71.0057 18.6668 71.4347 18.6895 71.9865C18.7122 72.5383 18.2833 73.004 17.7315 73.0268L17.6492 71.0285ZM21.6228 72.7881C21.0723 72.8334 20.5894 72.4239 20.5441 71.8735C20.4988 71.3231 20.9083 70.8402 21.4587 70.7949L21.6228 72.7881ZM25.3616 70.3823C25.9092 70.311 26.411 70.6972 26.4823 71.2448C26.5536 71.7925 26.1675 72.2943 25.6198 72.3656L25.3616 70.3823ZM29.5422 71.7512C28.999 71.8513 28.4775 71.4922 28.3774 70.9491C28.2773 70.406 28.6364 69.8845 29.1795 69.7843L29.5422 71.7512ZM32.9167 68.9826C33.4529 68.8505 33.9947 69.1781 34.1268 69.7144C34.2589 70.2506 33.9313 70.7924 33.3951 70.9246L32.9167 68.9826ZM37.2494 69.8377C36.7233 70.006 36.1605 69.7159 35.9923 69.1899C35.8241 68.6638 36.1141 68.101 36.6402 67.9328L37.2494 69.8377ZM40.266 66.6213C40.7776 66.4133 41.3609 66.6594 41.569 67.171C41.777 67.6826 41.5309 68.266 41.0193 68.474L40.266 66.6213ZM44.6612 66.8114C44.1693 67.0624 43.567 66.8671 43.316 66.3752C43.065 65.8833 43.2603 65.281 43.7522 65.0299L44.6612 66.8114ZM47.0694 63.1378C47.5355 62.8414 48.1535 62.9789 48.4499 63.4449C48.7463 63.911 48.6087 64.529 48.1427 64.8254L47.0694 63.1378ZM51.4182 62.5008C50.985 62.8434 50.3561 62.7699 50.0135 62.3367C49.671 61.9035 49.7444 61.2746 50.1776 60.932L51.4182 62.5008ZM53.0322 58.4133C53.4258 58.0258 54.0589 58.0308 54.4464 58.4243C54.8339 58.8179 54.8289 59.451 54.4354 59.8385L53.0322 58.4133ZM57.1442 56.8582C56.796 57.2869 56.1662 57.3522 55.7375 57.004C55.3088 56.6558 55.2435 56.026 55.5917 55.5973L57.1442 56.8582ZM57.8249 52.5142C58.124 52.0499 58.7428 51.9159 59.2071 52.2149C59.6714 52.514 59.8054 53.1328 59.5064 53.5971L57.8249 52.5142ZM61.5017 50.1042C61.2531 50.5974 60.6518 50.7957 60.1586 50.5471C59.6655 50.2986 59.4671 49.6972 59.7157 49.2041L61.5017 50.1042ZM61.2629 45.7115C61.4619 45.1963 62.0409 44.94 62.5561 45.139C63.0713 45.3379 63.3276 45.9169 63.1286 46.4321L61.2629 45.7115ZM64.4011 42.6309C64.249 43.1618 63.6954 43.469 63.1644 43.3169C62.6335 43.1649 62.3263 42.6112 62.4783 42.0803L64.4011 42.6309ZM63.3821 38.3501C63.4908 37.8086 64.0179 37.4577 64.5593 37.5664C65.1008 37.6751 65.4517 38.2021 65.3431 38.7436L63.3821 38.3501ZM65.984 34.801C65.9148 35.3489 65.4145 35.737 64.8666 35.6678C64.3186 35.5986 63.9305 35.0984 63.9997 34.5504L65.984 34.801ZM64.3567 30.7073C64.3903 30.1561 64.8644 29.7364 65.4156 29.77C65.9669 29.8036 66.3866 30.2777 66.353 30.8289L64.3567 30.7073ZM66.4777 26.8484C66.4762 27.4006 66.0272 27.8471 65.4749 27.8455C64.9226 27.8439 64.4762 27.395 64.4777 26.8427L66.4777 26.8484ZM64.386 22.9743C64.3587 22.4227 64.7838 21.9535 65.3354 21.9262C65.887 21.8989 66.3563 22.324 66.3835 22.8756L64.386 22.9743ZM66.0933 18.9276C66.1466 19.4773 65.7442 19.9662 65.1945 20.0195C64.6448 20.0728 64.156 19.6704 64.1026 19.1207L66.0933 18.9276ZM63.6409 15.2536C63.5636 14.7067 63.9443 14.2007 64.4911 14.1235C65.038 14.0462 65.5439 14.4268 65.6212 14.9737L63.6409 15.2536ZM64.9878 11.0689C65.0871 11.6122 64.7272 12.1331 64.1839 12.2324C63.6406 12.3317 63.1197 11.9718 63.0204 11.4285L64.9878 11.0689ZM62.2454 7.60575C62.1255 7.06664 62.4653 6.5324 63.0044 6.4125C63.5435 6.29259 64.0778 6.63242 64.1977 7.17154L62.2454 7.60575ZM63.2774 3.34486C63.4146 3.87983 63.0922 4.42474 62.5572 4.56194C62.0222 4.69914 61.4773 4.37669 61.3401 3.84171L63.2774 3.34486ZM5.9838 70.8908C7.3092 70.9696 8.60547 71.0272 9.87321 71.0644L9.81453 73.0635C8.52622 73.0257 7.20996 72.9672 5.86516 72.8873L5.9838 70.8908ZM13.74 71.115C15.0769 71.1099 16.3797 71.0808 17.6492 71.0285L17.7315 73.0268C16.4364 73.0801 15.1086 73.1098 13.7475 73.115L13.74 71.115ZM21.4587 70.7949C22.7999 70.6845 24.1005 70.5466 25.3616 70.3823L25.6198 72.3656C24.3264 72.534 22.9943 72.6753 21.6228 72.7881L21.4587 70.7949ZM29.1795 69.7843C30.4705 69.5463 31.7158 69.2785 32.9167 68.9826L33.3951 70.9246C32.1546 71.2302 30.8707 71.5062 29.5422 71.7512L29.1795 69.7843ZM36.6402 67.9328C37.9035 67.5288 39.1113 67.0908 40.266 66.6213L41.0193 68.474C39.8159 68.9633 38.5599 69.4186 37.2494 69.8377L36.6402 67.9328ZM43.7522 65.0299C44.92 64.4341 46.0247 63.8022 47.0694 63.1378L48.1427 64.8254C47.0437 65.5243 45.8841 66.1874 44.6612 66.8114L43.7522 65.0299ZM50.1776 60.932C51.196 60.1267 52.1462 59.2856 53.0322 58.4133L54.4354 59.8385C53.4978 60.7615 52.4934 61.6505 51.4182 62.5008L50.1776 60.932ZM55.5917 55.5973C56.4024 54.5991 57.1452 53.5697 57.8249 52.5142L59.5064 53.5971C58.788 54.7126 58.0023 55.8016 57.1442 56.8582L55.5917 55.5973ZM59.7157 49.2041C60.292 48.0604 60.8061 46.8943 61.2629 45.7115L63.1286 46.4321C62.6493 47.6731 62.1089 48.8993 61.5017 50.1042L59.7157 49.2041ZM62.4783 42.0803C62.8319 40.8457 63.1313 39.6002 63.3821 38.3501L65.3431 38.7436C65.0823 40.0433 64.7703 41.3414 64.4011 42.6309L62.4783 42.0803ZM63.9997 34.5504C64.1621 33.2645 64.2791 31.9811 64.3567 30.7073L66.353 30.8289C66.2729 32.143 66.1521 33.4694 65.984 34.801L63.9997 34.5504ZM64.4777 26.8427C64.4815 25.5312 64.4485 24.239 64.386 22.9743L66.3835 22.8756C66.4476 24.1727 66.4816 25.4998 66.4777 26.8484L64.4777 26.8427ZM64.1026 19.1207C63.9728 17.782 63.8155 16.4891 63.6409 15.2536L65.6212 14.9737C65.7996 16.2358 65.9604 17.5577 66.0933 18.9276L64.1026 19.1207ZM63.0204 11.4285C62.7651 10.0318 62.4998 8.74959 62.2454 7.60575L64.1977 7.17154C64.4568 8.33684 64.7274 9.64416 64.9878 11.0689L63.0204 11.4285ZM61.3401 3.84171C61.117 2.97162 60.9362 2.2952 60.8543 1.92501L62.8071 1.49301C62.8794 1.81983 63.0423 2.42805 63.2774 3.34486L61.3401 3.84171Z"
                    fill-opacity="0.41"
                    fill={filterBackgroundColor(resolvedBackgroundColor, textColor)}
                  />
                </SvgArrow>
              </>
            )}
          </FlexBox>
        </ButtonInner>
      </StyledWrapper>

      <ImageGallery count={galleryimages.length}>
        {galleryimages.slice(0, galleryimages.length / 2).map((image, index) => (
          <div className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`} key={index}>
            <img src={image} alt={`Image ${index + 1}`} />
          </div>
        ))}
        <div className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`}>
          {bookMockup ? (
            <HoverableDropzone
              isSalePage
              css={
                galleryimages.length > 1 ? (imageStyles as never) : (imageStylesOnlyMockup as never)
              }
              disabled={!editable}
              resizeSelector={editable}
              enableLinks
              updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
              handleResize={({ width, height }) => {
                setResize({ width, height });
                updateImageMeta(meta['accessorKey'], width, 'width');
                updateImageMeta(meta['accessorKey'], height, 'height');
              }}
              allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
              onUpload={handleUpload}
              loading={isLoading}
              alreadyUploaded={Boolean(bookMockup)}
              imageFile={bookMockup}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              positionEnabled
              handlePosition={(width: number, height: number, x: number, y: number) => {
                updateImageMeta(meta['accessorKey'], x, 'x_1');
                updateImageMeta(meta['accessorKey'], y, 'y_1');
                updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                updateImageMeta(meta['accessorKey'], height, 'd_height_1');
              }}
              imageAspectRatio={{
                ratio: '3x4',
                pixels: '600x800'
              }}>
              <BookMediaImg
                isImageUploaded={isImageUploaded}
                src={bookMockup}
                alt="Book Mockup"
                width={resize.width || 500}
                height={resize.height || 500}
              />
            </HoverableDropzone>
          ) : (
            <StyledMockup
              mockup={mockup?.file}
              mockup_id={mockup?.id}
              cover_thumbnail={coverThumbnail}
            />
          )}
        </div>
        {galleryimages.slice(galleryimages.length / 2, galleryimages.length).map((image, index) => (
          <div className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`} key={index}>
            <img src={image} alt={`Image ${index + 1}`} />
          </div>
        ))}
      </ImageGallery>
      <BenefitWrapper>
        <Wrapper>
          <Benefits>
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              return (
                <BenefitItem
                  // style={{
                  //   display: isTitleBenEmpty && isContentBenEmpty && !editable ? 'flex' : 'none'
                  // }}
                  // TODO: Replace with dynamic values later
                  key={`${icon?.toString()}-${index}`}>
                  <StyledSvgIcons
                    iconName={`icon_${index + 1}` as 'icon_1' | 'icon_2' | 'icon_3'}
                    backgroundColor={iconColor}
                  />
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="title"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isTitleBenEmpty}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(content?.val, editable)
                      }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="content"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isContentBenEmpty}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </Wrapper>
      </BenefitWrapper>

      <WhiteInner>
        <RelativeWhite>
          <div className="hero-white-v5 hero-white-v5_two"></div>
          <div className="hero-white-v5 hero-white-v5_three"></div>
          <div className="hero-white-v5 hero-white-v5_five"></div>
          <div className="hero-white-v5 hero-white-v5_six"></div>
        </RelativeWhite>
      </WhiteInner>
      <ColoredBg style={{ background: resolvedBackgroundColor }}>
        <DottedBg>
          <DotsBg isWhiteBg={meta?.['background_color']?.includes('background_color4')} />
        </DottedBg>
      </ColoredBg>
    </HeroDetailed>
  );
};
